import type { CSSProperties, ReactElement, ReactNode } from 'react';
import { COULEURS, styleBouton } from '../../app/styles.js';
import { AlertePartage } from './AlertePartage.js';
import { CANAUX_RECUEIL, ETATS_CONSENTEMENT, LIGNES_CONSENTEMENT, TYPES_COORDONNEE } from './libelles.js';
import {
  dateCourte,
  etatConsentement,
  libelleCanal,
  libelleStatutPiece,
  masquerCoordonnee,
  type CanalConsentement,
  type CorpsPartage,
  type DonneesFiche,
  type FinaliteConsentement,
} from './modele.js';

export interface VueFiche {
  coordonneesVisibles: boolean;
  /** Identifiant de l'étiquette dont l'alerte de partage est ouverte. */
  alerte: string | null;
  /** Clés des destinataires cochés dans l'alerte de partage (vide à l'ouverture). */
  destinatairesCoches: readonly string[];
  /** Enregistrement d'un consentement en cours : le canal de recueil est choisi par l'équipe, jamais déduit. */
  consentement: { finalite: FinaliteConsentement; accorde: boolean; canal: CanalConsentement | null } | null;
  message: string | null;
}
export interface ActionsFiche {
  onAfficherCoordonnees: () => void;
  onOuvrirAlerte: (etiquetteId: string) => void;
  onFermerAlerte: () => void;
  onConfirmerPartage: (etiquetteId: string, corps: CorpsPartage) => void | Promise<void>;
  onPause: (planId: string) => void | Promise<void>;
  onReprise: (planId: string) => void | Promise<void>;
  onBasculerDestinataire: (cle: string) => void;
  onOuvrirConsentement: (finalite: FinaliteConsentement, accorde: boolean) => void;
  onChoisirCanal: (canal: CanalConsentement) => void;
  onConfirmerConsentement: () => void | Promise<void>;
  onAnnulerConsentement: () => void;
}
export interface PropsFicheHeritier {
  donnees: DonneesFiche;
  vue: VueFiche;
  actions: ActionsFiche;
}

const styleSection: CSSProperties = {
  border: `1px solid ${COULEURS.bordure}`,
  borderRadius: 8,
  background: COULEURS.blanc,
  padding: 16,
  margin: '0 0 16px',
};
const styleTitre: CSSProperties = { margin: '0 0 8px', fontSize: 20, color: COULEURS.marine };
const styleListe: CSSProperties = { listStyle: 'none', margin: 0, padding: 0 };
const styleLigne: CSSProperties = { padding: '6px 0' };
const styleSecondaire: CSSProperties = { color: COULEURS.ardoise, margin: '2px 0' };
const styleActions: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 };

const styleChoixCanal: CSSProperties = { border: `1px solid ${COULEURS.bordure}`, borderRadius: 8, padding: 12, margin: '8px 0' };
const styleCanal: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, minHeight: 44, cursor: 'pointer' };

const Section = ({ titre, children }: { titre: string; children: ReactNode }): ReactElement => (
  <section style={styleSection}>
    <h2 style={styleTitre}>{titre}</h2>
    {children}
  </section>
);

const nomComplet = (d: DonneesFiche): string => `${d.personne.firstNames} ${d.personne.lastName}`.trim();

function anneeOuDate(naissance: DonneesFiche['personne']['birthDate']): string {
  if (naissance.value === null) return 'non renseignée';
  return naissance.precision === 'jour' ? dateCourte(naissance.value) : naissance.value;
}

/**
 * Fiche héritier (D05), vue pure réservée à l'équipe MAAT : état civil, coordonnées masquées, informations
 * sensibles et partage avec alerte, consentements détaillés, pièces attendues, échanges et relances.
 * Aucune somme, aucune estimation. Composant sans état : il s'appelle comme une fonction.
 */
export function FicheHeritier({ donnees, vue, actions }: PropsFicheHeritier): ReactElement {
  const { personne } = donnees;
  const alerte = vue.alerte === null ? null : donnees.sensibles.find((s) => s.id === vue.alerte);
  return (
    <div data-composant="fiche-heritier-contenu">
      <header style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 24, color: COULEURS.marine }}>{nomComplet(donnees)}</h2>
        <p style={styleSecondaire}>{personne.relationLabel ?? 'Lien de parenté à préciser'}</p>
      </header>
      {vue.message ? (
        <p role="status" style={{ ...styleSection, borderColor: COULEURS.indigo }}>
          {vue.message}
        </p>
      ) : null}

      <Section titre="État civil et coordonnées">
        <ul style={styleListe}>
          <li
            style={styleLigne}
          >{`Naissance : ${anneeOuDate(personne.birthDate)}${personne.birthPlace ? ` · ${personne.birthPlace}` : ''}`}</li>
          {personne.contacts.map((c) => (
            <li key={c.id} style={styleLigne}>
              {`${TYPES_COORDONNEE[c.type] ?? c.type} : ${vue.coordonneesVisibles ? c.value : masquerCoordonnee(c.type, c.value)}`}
            </li>
          ))}
          <li style={styleLigne}>
            <span>Canal préféré</span> <strong>{libelleCanal(personne.preferences.preferredChannel)}</strong>
          </li>
        </ul>
        <p style={styleSecondaire}>Visible de l’équipe MAAT seulement.</p>
        {vue.coordonneesVisibles ? null : (
          <div style={styleActions}>
            <button type="button" style={styleBouton} onClick={actions.onAfficherCoordonnees}>
              Afficher les coordonnées
            </button>
          </div>
        )}
      </Section>

      <Section titre="Informations sensibles">
        <p style={styleSecondaire}>visible : équipe MAAT seulement</p>
        {donnees.sensibles.length === 0 ? <p>Aucune information sensible enregistrée.</p> : null}
        <ul style={styleListe}>
          {donnees.sensibles.map((s) => (
            <li key={s.id} style={styleLigne}>
              <strong>{s.label}</strong>
              {s.note ? <p style={styleSecondaire}>{s.note}</p> : null}
              {s.shares
                .filter((p) => p.revokedAt === null)
                .map((p) => (
                  <p key={p.recipientDisplayName} style={styleSecondaire}>
                    {`Partagé avec ${p.recipientDisplayName} le ${dateCourte(p.grantedAt)}`}
                  </p>
                ))}
              <div style={styleActions}>
                <button type="button" style={styleBouton} onClick={() => actions.onOuvrirAlerte(s.id)}>
                  Partager hors équipe…
                </button>
              </div>
            </li>
          ))}
        </ul>
        {alerte ? (
          <AlertePartage
            etiquette={{ id: alerte.id, label: alerte.label }}
            destinataires={donnees.destinataires}
            selection={vue.destinatairesCoches}
            indisponible={donnees.partageIndisponible}
            onBasculer={actions.onBasculerDestinataire}
            onConfirmer={actions.onConfirmerPartage}
            onAnnuler={actions.onFermerAlerte}
          />
        ) : null}
      </Section>

      <Section titre="Consentements">
        <ul style={styleListe}>
          {LIGNES_CONSENTEMENT.map((ligne) => {
            const etat = etatConsentement(donnees.consentements, ligne.finalite);
            return (
              <li key={ligne.finalite} style={styleLigne}>
                <span>{`${ligne.libelle} : ${ETATS_CONSENTEMENT[etat.etat]}`}</span>
                {etat.capturedAt ? (
                  <p style={styleSecondaire}>{`Recueilli le ${dateCourte(etat.capturedAt)} par ${libelleCanal(etat.canal)}`}</p>
                ) : null}
                {ligne.court ? (
                  <div style={styleActions}>
                    <button type="button" style={styleBouton} onClick={() => actions.onOuvrirConsentement(ligne.finalite, true)}>
                      {`${ligne.court} : accepte`}
                    </button>
                    <button type="button" style={styleBouton} onClick={() => actions.onOuvrirConsentement(ligne.finalite, false)}>
                      {`${ligne.court} : refuse`}
                    </button>
                  </div>
                ) : null}
                {vue.consentement && vue.consentement.finalite === ligne.finalite ? (
                  <fieldset style={styleChoixCanal}>
                    <legend>{`Canal de recueil : ${ligne.court ?? ligne.libelle}, ${vue.consentement.accorde ? 'accepte' : 'refuse'}`}</legend>
                    <p style={styleSecondaire}>Par quel canal la personne vous a-t-elle donné ce choix ? Il sera conservé comme preuve.</p>
                    {CANAUX_RECUEIL.map((code) => (
                      <label key={code} style={styleCanal}>
                        <input
                          type="radio"
                          name={`canal-${ligne.finalite}`}
                          value={code}
                          checked={vue.consentement?.canal === code}
                          onChange={() => actions.onChoisirCanal(code as CanalConsentement)}
                          style={{ width: 24, height: 24 }}
                        />
                        <span>{libelleCanal(code)}</span>
                      </label>
                    ))}
                    <div style={styleActions}>
                      <button
                        type="button"
                        disabled={vue.consentement.canal === null}
                        style={vue.consentement.canal === null ? { ...styleBouton, opacity: 0.5, cursor: 'not-allowed' } : styleBouton}
                        onClick={() => {
                          if (vue.consentement?.canal != null) void actions.onConfirmerConsentement();
                        }}
                      >
                        Enregistrer le choix
                      </button>
                      <button type="button" style={styleBouton} onClick={actions.onAnnulerConsentement}>
                        Annuler
                      </button>
                    </div>
                  </fieldset>
                ) : null}
              </li>
            );
          })}
        </ul>
        <p style={styleSecondaire}>Chaque choix est daté et journalisé.</p>
      </Section>

      <Section titre="Pièces attendues">
        {donnees.pieces.length === 0 ? <p>Aucune pièce attendue.</p> : null}
        <ul style={styleListe}>
          {donnees.pieces.map((p) => (
            <li key={p.id} style={styleLigne}>
              <span>{p.label}</span> <strong>{libelleStatutPiece(p.status)}</strong>
            </li>
          ))}
        </ul>
      </Section>

      <Section titre="Échanges et relances">
        <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>À venir</h3>
        {donnees.plans.length === 0 ? <p>Aucune relance programmée.</p> : null}
        <ul style={styleListe}>
          {donnees.plans.map((p) => (
            <li key={p.id} style={styleLigne}>
              {p.state === 'actif' ? (
                <span>
                  {p.nextActionAt
                    ? `${p.cadenceName} : prochaine action le ${dateCourte(p.nextActionAt)} par ${libelleCanal(p.nextAction?.channel ?? null)}`
                    : `${p.cadenceName} : aucune action programmée`}
                </span>
              ) : (
                <span>{`Relances en pause${p.pausedReason ? ` : ${p.pausedReason}` : ''}${p.pausedUntil ? `, reprise le ${dateCourte(p.pausedUntil)}` : ''}`}</span>
              )}
              <div style={styleActions}>
                {p.state === 'pause' ? (
                  <button type="button" style={styleBouton} onClick={() => void actions.onReprise(p.id)}>
                    Reprendre les relances
                  </button>
                ) : p.state === 'actif' ? (
                  <button type="button" style={styleBouton} onClick={() => void actions.onPause(p.id)}>
                    Suspendre les relances
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
        <h3 style={{ margin: '12px 0 4px', fontSize: 16 }}>Déjà échangé</h3>
        {donnees.echangesIndisponible !== null ? (
          <p role="status">{donnees.echangesIndisponible}</p>
        ) : donnees.echanges.length === 0 ? (
          <p>Aucun échange pour le moment.</p>
        ) : null}
        <ul style={styleListe}>
          {donnees.echanges.map((e) => (
            <li key={e.id} style={styleLigne}>{`${dateCourte(e.lastMessageAt)} · ${e.channelAccount.label} · ${e.lastMessagePreview}`}</li>
          ))}
        </ul>
      </Section>
    </div>
  );
}
